import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
} from '@mui/material';
import { Refresh, CheckCircle, Schedule, ExpandMore, Link as LinkIcon, Settings, Edit } from '@mui/icons-material';
import { toast } from 'react-toastify';
import apiService, { Dataset } from '../services/api';
import { RENAISSANCE_COLORS } from '../utils';
import DatasetMappingConfig from './DatasetMappingConfig';

interface DatasetBrowserProps {
  onDatasetSelect?: (s3Path: string) => void;
  selectedDataset?: string;
}

const DatasetBrowser: React.FC<DatasetBrowserProps> = ({ onDatasetSelect, selectedDataset }) => {
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'csv' | 'parquet'>('all');
  const [s3Configured, setS3Configured] = useState<boolean | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [reconfigureDialogOpen, setReconfigureDialogOpen] = useState(false);
  const [datasetToReconfigure, setDatasetToReconfigure] = useState<Dataset | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [datasetToRename, setDatasetToRename] = useState<Dataset | null>(null);
  const [newDatasetName, setNewDatasetName] = useState<string>('');

  const loadDatasets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.listDatasets(filterType);
      if (response.status === 'success') {
        setDatasets(response.datasets);
      } else {
        setError('Failed to load datasets');
        toast.error('Failed to load datasets');
      }
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to load datasets';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  // Check S3 configuration on mount
  useEffect(() => {
    const checkConfig = async () => {
      try {
        const config = await apiService.checkDatasetsConfig();
        setS3Configured(config.configured);
        if (config.configured) {
          loadDatasets();
        }
      } catch (err) {
        setS3Configured(false);
      } finally {
        setLoadingConfig(false);
      }
    };
    checkConfig();
  }, [loadDatasets]);

  useEffect(() => {
    if (s3Configured) {
      loadDatasets();
    }
  }, [filterType, s3Configured, loadDatasets]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const handleSelectDataset = (dataset: Dataset) => {
    // Prefer parquet file if available, otherwise use CSV
    // Pass the key directly - backend will use S3_DATASETS_BUCKET from environment
    if (onDatasetSelect) {
      onDatasetSelect(dataset.parquet_key || dataset.key);
    }
  };

  const handleReconfigure = async (dataset: Dataset) => {
    if (!dataset.relationships || !dataset.relationships.source_files) {
      toast.error('This dataset does not have configuration data. Please upload it through the upload workflow.');
      return;
    }

    // Determine file types by checking S3 tags or inferring from column mappings
    const sourceFilesWithTypes: Array<{s3Key: string; fileType: 'items' | 'responses'; fileHash?: string}> = [];
    
    for (const s3Key of dataset.relationships.source_files) {
      let fileType: 'items' | 'responses' = 'responses';
      
      // Check if column mappings indicate this is an items file
      // If the file has mappings for items columns, it's likely an items file
      const itemsMappings = dataset.relationships.column_mappings?.items || {};

      // Infer from S3 key if it contains 'items' or 'responses'
      if (s3Key.toLowerCase().includes('item')) {
        fileType = 'items';
      } else if (s3Key.toLowerCase().includes('response')) {
        fileType = 'responses';
      } else {
        // If we have items mappings but no responses mappings, and only one file, it's items
        // Otherwise, default to responses
        if (dataset.relationships.source_files.length === 1 && Object.keys(itemsMappings).length > 0) {
          fileType = 'items';
        }
      }
      
      sourceFilesWithTypes.push({ s3Key, fileType });
    }

    // Convert column mappings from {items: {...}, responses: {...}} format
    // to {s3Key: {requiredCol: csvCol}} format
    const initialMappings: Record<string, Record<string, string>> = {};
    const itemsMappings = dataset.relationships.column_mappings?.items || {};
    const responsesMappings = dataset.relationships.column_mappings?.responses || {};
    
    // Find items file and apply items mappings
    const itemsFile = sourceFilesWithTypes.find(f => f.fileType === 'items');
    if (itemsFile) {
      initialMappings[itemsFile.s3Key] = itemsMappings;
    }
    
    // Apply responses mappings to all response files
    sourceFilesWithTypes.filter(f => f.fileType === 'responses').forEach(respFile => {
      initialMappings[respFile.s3Key] = responsesMappings;
    });

    setDatasetToReconfigure({
      ...dataset,
      // Store the processed files for the dialog
      _reconfigureFiles: sourceFilesWithTypes,
      _initialMappings: initialMappings,
    } as any);
    setReconfigureDialogOpen(true);
  };

  const handleReconfigureComplete = async (
    joinConfig: {
      join_type: 'inner' | 'left' | 'right' | 'outer';
      join_keys: {
        items: string[];
        responses: string[];
      };
    },
    columnMappings: {
      items: Record<string, string>;
      responses: Record<string, string>;
    }
  ) => {
    if (!datasetToReconfigure || !datasetToReconfigure.relationships) {
      return;
    }

    try {
      // Use the processed files from handleReconfigure
      const sourceFilesWithTypes = (datasetToReconfigure as any)._reconfigureFiles || 
        datasetToReconfigure.relationships.source_files.map((s3Key: string) => ({
          s3Key,
          fileType: s3Key.toLowerCase().includes('item') ? 'items' : 'responses' as 'items' | 'responses'
        }));

      // Convert to API format
      const files = sourceFilesWithTypes.map((file: {s3Key: string; fileType: 'items' | 'responses'}) => ({
        s3_key: file.s3Key,
        file_type: file.fileType,
      }));

      // Call the multi-file upload complete endpoint with new mappings
      await apiService.notifyMultiUploadComplete(files, joinConfig, columnMappings);

      toast.success('Reconfiguration and conversion started. The new Parquet file will be available shortly.');
      setReconfigureDialogOpen(false);
      setDatasetToReconfigure(null);
      
      // Refresh datasets after a delay
      setTimeout(() => loadDatasets(), 2000);
    } catch (err: any) {
      toast.error(`Reconfiguration failed: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleReconfigureCancel = () => {
    setReconfigureDialogOpen(false);
    setDatasetToReconfigure(null);
  };

  const handleRename = (dataset: Dataset) => {
    setDatasetToRename(dataset);
    setNewDatasetName(dataset.name || dataset.filename);
    setRenameDialogOpen(true);
  };

  const handleRenameConfirm = async () => {
    if (!datasetToRename || !newDatasetName.trim()) {
      toast.error('Please enter a valid dataset name');
      return;
    }

    try {
      await apiService.renameDataset(datasetToRename.parquet_key || datasetToRename.key, newDatasetName.trim());
      toast.success('Dataset renamed successfully');
      setRenameDialogOpen(false);
      setDatasetToRename(null);
      setNewDatasetName('');
      // Refresh datasets
      loadDatasets();
    } catch (err: any) {
      toast.error(`Failed to rename dataset: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleRenameCancel = () => {
    setRenameDialogOpen(false);
    setDatasetToRename(null);
    setNewDatasetName('');
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: 'primary.main' }}>
          Available Datasets
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filterType}
              label="Type"
              onChange={(e) => setFilterType(e.target.value as 'all' | 'csv' | 'parquet')}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="csv">CSV</MenuItem>
              <MenuItem value="parquet">Parquet</MenuItem>
            </Select>
          </FormControl>
          <IconButton onClick={loadDatasets} disabled={loading}>
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {(loadingConfig || loading) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {!loadingConfig && !s3Configured && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          S3 dataset storage is not configured. Please configure S3_DATASETS_BUCKET to enable dataset browsing.
        </Alert>
      )}

      {!loadingConfig && s3Configured && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loadingConfig && s3Configured && !loading && !error && datasets.length === 0 && (
        <Alert severity="info">
          No datasets found. Upload a CSV file to get started.
        </Alert>
      )}

      {!loading && !error && datasets.length > 0 && (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Filename</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Upload Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {datasets.map((dataset) => {
                const isSelected = selectedDataset === (dataset.parquet_key || dataset.key);
                const hasParquet = !!dataset.parquet_key;
                const isCsv = dataset.type === 'csv';
                const hasRelationships = !!dataset.relationships;

                return (
                  <React.Fragment key={dataset.key}>
                    <TableRow
                      sx={{
                        backgroundColor: isSelected ? RENAISSANCE_COLORS.beige : 'transparent',
                        '&:hover': { backgroundColor: RENAISSANCE_COLORS.beige },
                      }}
                    >
                      <TableCell>{dataset.name || dataset.filename}</TableCell>
                      <TableCell>
                        <Chip
                          label={dataset.type.toUpperCase()}
                          size="small"
                          color={dataset.type === 'parquet' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{formatFileSize(dataset.size)}</TableCell>
                      <TableCell>{formatDate(dataset.upload_date)}</TableCell>
                      <TableCell>
                        {isCsv ? (
                          hasParquet ? (
                            <Chip
                              icon={<CheckCircle />}
                              label="Converted"
                              size="small"
                              color="success"
                            />
                          ) : (
                            <Chip
                              icon={<Schedule />}
                              label="Pending"
                              size="small"
                              color="warning"
                            />
                          )
                        ) : (
                          <Chip
                            icon={<CheckCircle />}
                            label="Ready"
                            size="small"
                            color="success"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {(hasParquet || !isCsv) && (
                              <>
                                <Button
                                  size="small"
                                  variant={isSelected ? 'contained' : 'outlined'}
                                  onClick={() => handleSelectDataset(dataset)}
                                >
                                  {isSelected ? 'Selected' : 'Select'}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Edit />}
                                  onClick={() => handleRename(dataset)}
                                >
                                  Rename
                                </Button>
                                {hasRelationships && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Settings />}
                                    onClick={() => handleReconfigure(dataset)}
                                  >
                                    Reconfigure
                                  </Button>
                                )}
                              </>
                            )}
                            {isCsv && !hasParquet && (
                              <Alert severity="info" sx={{ p: 1, fontSize: '0.75rem' }}>
                                Upload via Upload page to configure mappings
                              </Alert>
                            )}
                          </Box>
                          {hasRelationships && (
                            <Chip
                              icon={<LinkIcon />}
                              label="Multi-file"
                              size="small"
                              color="info"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    {hasRelationships && dataset.relationships && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0, border: 'none' }}>
                          <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                              <Typography variant="body2" sx={{ color: 'primary.main' }}>
                                File Relationships & Configuration
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Box sx={{ pl: 2 }}>
                                {dataset.relationships.source_files && dataset.relationships.source_files.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Source Files:
                                    </Typography>
                                    <List dense>
                                      {dataset.relationships.source_files.map((sourceKey: string, idx: number) => (
                                        <ListItem key={idx} sx={{ py: 0.5 }}>
                                          <ListItemText
                                            primary={sourceKey.split('/').pop()}
                                            secondary={sourceKey}
                                            primaryTypographyProps={{ variant: 'body2' }}
                                            secondaryTypographyProps={{ variant: 'caption' }}
                                          />
                                        </ListItem>
                                      ))}
                                    </List>
                                  </Box>
                                )}
                                {dataset.relationships.join_config && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Join Configuration:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: RENAISSANCE_COLORS.mediumgrey }}>
                                      Type: <strong>{dataset.relationships.join_config.join_type || 'inner'}</strong>
                                    </Typography>
                                    {dataset.relationships.join_config.join_keys && (
                                      <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption" sx={{ color: RENAISSANCE_COLORS.mediumgrey }}>
                                          Items: {dataset.relationships.join_config.join_keys.items?.join(', ') || 'N/A'}
                                        </Typography>
                                        <br />
                                        <Typography variant="caption" sx={{ color: RENAISSANCE_COLORS.mediumgrey }}>
                                          Responses: {dataset.relationships.join_config.join_keys.responses?.join(', ') || 'N/A'}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                )}
                                {dataset.relationships.column_mappings && (
                                  <Box>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Column Mappings:
                                    </Typography>
                                    {Object.entries(dataset.relationships.column_mappings).map(([fileType, mappings]: [string, any]) => (
                                      <Box key={fileType} sx={{ mb: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                          {fileType === 'items' ? 'Items File' : 'Response Files'}:
                                        </Typography>
                                        <Box sx={{ pl: 2 }}>
                                          {Object.entries(mappings).map(([target, source]: [string, any]) => (
                                            <Typography key={target} variant="caption" sx={{ display: 'block', color: RENAISSANCE_COLORS.mediumgrey }}>
                                              {target} ← {source}
                                            </Typography>
                                          ))}
                                        </Box>
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Reconfigure Dialog */}
      <Dialog
        open={reconfigureDialogOpen}
        onClose={handleReconfigureCancel}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Reconfigure Dataset: {datasetToReconfigure?.filename}
        </DialogTitle>
        <DialogContent>
          {datasetToReconfigure && datasetToReconfigure.relationships && (() => {
            const relationships = datasetToReconfigure.relationships!;
            const joinConfig = relationships.join_config as any;
            const initialJoinConfig = joinConfig ? {
              join_type: (joinConfig.join_type || 'inner') as 'inner' | 'left' | 'right' | 'outer',
              join_keys: {
                items: Array.isArray(joinConfig.join_keys?.items) ? joinConfig.join_keys.items : [],
                responses: Array.isArray(joinConfig.join_keys?.responses) ? joinConfig.join_keys.responses : []
              }
            } : undefined;
            
            return (
              <DatasetMappingConfig
                uploadedFiles={(datasetToReconfigure as any)._reconfigureFiles || 
                  (relationships.source_files || []).map((s3Key: string) => ({
                    s3Key,
                    fileType: (s3Key.toLowerCase().includes('item') ? 'items' : 'responses') as 'items' | 'responses'
                  }))}
                initialJoinConfig={initialJoinConfig}
                initialColumnMappings={(datasetToReconfigure as any)._initialMappings}
                onComplete={handleReconfigureComplete}
                onCancel={handleReconfigureCancel}
              />
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={handleRenameCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rename Dataset</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Dataset Name"
            fullWidth
            variant="outlined"
            value={newDatasetName}
            onChange={(e) => setNewDatasetName(e.target.value)}
            placeholder="Enter a friendly name for this dataset"
          />
          {datasetToRename && (
            <Typography variant="caption" sx={{ color: RENAISSANCE_COLORS.mediumgrey, mt: 1, display: 'block' }}>
              Current filename: {datasetToRename.filename}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRenameCancel}>Cancel</Button>
          <Button onClick={handleRenameConfirm} variant="contained" disabled={!newDatasetName.trim()}>
            Rename
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DatasetBrowser;
